import {
  MATCH_MODES,
  type FilterMatchMode,
  type ReportFilter,
  type SavedReportFilterInput,
  type SavedReportFilterOption,
} from '../models/filter.types'

import { getReportFilterModeDefinition } from './reportFilterModes'

const MATCH_MODE_SET = new Set<FilterMatchMode>(MATCH_MODES)
const COLUMN_SUB_TYPES = new Set<NonNullable<ReportFilter['sub_type']>>([
  'currency',
  'numeric',
  'decimal',
  'percentage',
  'meta',
  'date',
  'datetime',
  'image',
  'wsName',
  'status',
  'array',
  'text',
])

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value)
}

function hasScalarValue(value: unknown): value is string | number | boolean {
  return (
    (typeof value === 'string' && value.trim() !== '') ||
    (typeof value === 'number' && Number.isFinite(value)) ||
    typeof value === 'boolean'
  )
}

function numericSubType(subType: ReportFilter['sub_type']): boolean {
  return ['currency', 'numeric', 'decimal', 'percentage', 'meta'].includes(subType ?? '')
}

function normalizedScalar(value: unknown, numeric: boolean): string | number | boolean | null {
  if (!hasScalarValue(value)) return null
  if (!numeric) return value
  const parsed = Number(value)
  return Number.isFinite(parsed) ? parsed : null
}

function normalizedValue(mode: FilterMatchMode, value: unknown, numeric: boolean): unknown | null {
  const shape = getReportFilterModeDefinition(mode).valueShape
  if (shape === 'none') return null
  if (shape === 'single') return normalizedScalar(value, numeric)

  const source = Array.isArray(value) ? value : typeof value === 'string' ? value.split(',') : []
  const values = source
    .map((item) => (typeof item === 'string' ? item.trim() : item))
    .filter((item) => item !== '')
  if (shape === 'range' ? values.length !== 2 : values.length === 0) return null
  const normalized = values.map((item) => normalizedScalar(item, numeric))
  return normalized.some((item) => item === null) ? null : normalized
}

function normalizeFilter(value: unknown): ReportFilter | null {
  if (!isRecord(value)) return null
  const field = typeof value.field === 'string' ? value.field.trim() : ''
  const mode = value.match_mode
  if (!field || typeof mode !== 'string' || !MATCH_MODE_SET.has(mode as FilterMatchMode))
    return null
  const matchMode = mode as FilterMatchMode
  const subType =
    typeof value.sub_type === 'string' &&
    COLUMN_SUB_TYPES.has(value.sub_type as NonNullable<ReportFilter['sub_type']>)
      ? (value.sub_type as NonNullable<ReportFilter['sub_type']>)
      : undefined
  const normalized = normalizedValue(matchMode, value.value, numericSubType(subType))
  if (normalized === null && getReportFilterModeDefinition(matchMode).valueShape !== 'none') {
    return null
  }
  const isChange = value.is_change === true
  const direction =
    value.direction === 'increase' || value.direction === 'decrease' ? value.direction : undefined
  if (isChange && direction === undefined) return null

  return {
    field,
    match_mode: matchMode,
    value: normalized,
    ...(typeof value.is_change === 'boolean' ? { is_change: value.is_change } : {}),
    ...(isChange && direction ? { direction } : {}),
    ...(subType === undefined ? {} : { sub_type: subType }),
  }
}

function nameKey(name: string): string {
  return name.trim().toLowerCase()
}

function savedId(value: unknown, fallback: string): string {
  return typeof value === 'string' && value.trim() ? value.trim() : `legacy:${fallback}`
}

/** Defensive parser for the unversioned legacy `save_filters` workspace-preference value. */
export function parseSavedReportFilters(value: unknown): SavedReportFilterOption[] {
  if (!Array.isArray(value)) return []
  const seenNames = new Set<string>()
  const seenIds = new Set<string>()
  const parsed: SavedReportFilterOption[] = []

  for (const item of value) {
    if (!isRecord(item) || typeof item.name !== 'string') continue
    const name = item.name.trim()
    const filter = normalizeFilter(isRecord(item.filter) ? item.filter : item)
    const key = nameKey(name)
    if (!name || !filter || seenNames.has(key)) continue
    let id = savedId(item.id, key)
    let suffix = 2
    while (seenIds.has(id)) {
      id = `${savedId(item.id, key)}:${suffix}`
      suffix += 1
    }
    seenNames.add(key)
    seenIds.add(id)
    parsed.push({ id, name, filter })
  }
  return parsed
}

/** Preserve flat legacy fields so old and v4 frontends can share the preference during cutover. */
export function serializeSavedReportFilters(
  value: readonly SavedReportFilterOption[],
): Array<ReportFilter & { id: string; name: string }> {
  return value.map((item) => ({
    id: item.id,
    name: item.name,
    ...item.filter,
    value: Array.isArray(item.filter.value) ? [...item.filter.value] : item.filter.value,
  }))
}

export type SavedFilterMutationErrorCode = 'invalid_name' | 'duplicate_name' | 'not_found'

export class SavedFilterMutationError extends Error {
  constructor(readonly code: SavedFilterMutationErrorCode) {
    super(code)
    this.name = 'SavedFilterMutationError'
  }
}

function validateInput(
  input: SavedReportFilterInput,
  fallbackId?: string,
): SavedReportFilterOption {
  const name = input.name.trim()
  const filter = normalizeFilter(input.filter)
  if (!name || !filter) throw new SavedFilterMutationError('invalid_name')
  return { id: savedId(input.id, fallbackId ?? nameKey(name)), name, filter }
}

/** Add a legacy-compatible named filter without permitting ambiguous case-only duplicates. */
export function addSavedReportFilter(
  current: readonly SavedReportFilterOption[],
  input: SavedReportFilterInput,
): SavedReportFilterOption[] {
  const next = validateInput(input)
  if (current.some((item) => nameKey(item.name) === nameKey(next.name) || item.id === next.id)) {
    throw new SavedFilterMutationError('duplicate_name')
  }
  return [...current, next]
}

export function updateSavedReportFilter(
  current: readonly SavedReportFilterOption[],
  id: string,
  input: SavedReportFilterInput,
): SavedReportFilterOption[] {
  const index = current.findIndex((item) => item.id === id)
  if (index < 0) throw new SavedFilterMutationError('not_found')
  const next = validateInput(input, id)
  next.id = id
  const nextKey = nameKey(next.name)
  if (current.some((item, itemIndex) => itemIndex !== index && nameKey(item.name) === nextKey)) {
    throw new SavedFilterMutationError('duplicate_name')
  }
  return current.map((item, itemIndex) => (itemIndex === index ? next : item))
}

export function removeSavedReportFilter(
  current: readonly SavedReportFilterOption[],
  id: string,
): SavedReportFilterOption[] {
  const next = current.filter((item) => item.id !== id)
  if (next.length === current.length) throw new SavedFilterMutationError('not_found')
  return next
}
